import React from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../authentication/authenticationSlice";
import {
  selectSampleDataActive,
  selectSampleDataMutationStatus,
} from "../sample-data-slice";
import { loadSampleData } from "../slice-extensions/sample-data-slice-extension";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";

/** The quiet offer's action: a text link, the second action of experience-standard §1.6. */
const QUIET_BUTTON_CLASS =
  "shrink-0 text-sm font-medium text-primary-40 dark:text-primary-90 hover:underline disabled:opacity-50";

/**
 * The eye-catching "explore with sample data" invitation — a tinted panel in
 * the sample-data accent color (same family as the badge and banner) with a
 * primary action. Renders nothing for non-admins or while a set is active.
 * A successful load remounts the app so every cached list picks up the data.
 * `quiet` renders a plain row with a text link instead.
 */
const LoadSampleDataOffer: React.FC<{ className?: string; quiet?: boolean }> = ({
  className,
  quiet = false,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isOrgAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const active = useAppSelector(selectSampleDataActive);
  const busy = useAppSelector(selectSampleDataMutationStatus) === LoadingStatus.Loading;

  if (!isOrgAdmin || active) return null;

  const handleLoad = async () => {
    const result = await dispatch(loadSampleData({ languageCode: i18n.language }));
    if (loadSampleData.fulfilled.match(result)) {
      window.location.reload();
    }
  };

  // Quiet: a plain row with a text link, for screens where another card holds the one button.
  const panelClass = quiet ? "" : "rounded-xl bg-secondary-95 p-4 text-left dark:bg-secondary-20";
  const titleClass = quiet ? "text-grey-10 dark:text-grey-90" : "text-secondary-30 dark:text-secondary-80";
  const textClass = quiet ? "text-grey-30 dark:text-grey-70" : "text-secondary-30 dark:text-secondary-80";

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 ${panelClass} ${className ?? ""}`.trim()}
    >
      <div className="flex min-w-0 items-center gap-3">
        {!quiet && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-secondary-30 dark:bg-primary-10 dark:text-secondary-80">
            <FlaskConical size={20} strokeWidth={2} />
          </span>
        )}
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${titleClass}`}>
            {t("sampleData.offerTitle", "Explore with sample data")}
          </p>
          <p className={`text-sm ${textClass}`}>
            {t(
              "sampleData.checklistOffer",
              "Not ready to enter your own data? Explore with a ready-made example first.",
            )}
          </p>
        </div>
      </div>
      <button
        type="button"
        className={quiet ? QUIET_BUTTON_CLASS : "btn btn-primary shrink-0"}
        disabled={busy}
        onClick={handleLoad}
      >
        {busy
          ? t("sampleData.loading", "Setting up sample data…")
          : t("sampleData.loadAction", "Load sample data")}
      </button>
    </div>
  );
};

export default LoadSampleDataOffer;
